import type { Action, NodeEditorTab } from 'flow/store/engine/types';
import { panes } from './DiffContext';
import type { ReactNode } from 'react';
import type { Store } from '@reduxjs/toolkit';
import type { State } from '~/store';
import { FormProvider } from 'react-hook-form';
import { Provider as ReduxStoreProvider } from 'react-redux';
import useNodeEditorForm from '../../NodeEditorDrawer/useNodeEditorForm';
import { StyledDiffViewCardSlot } from './styles';

type DiffCardFormProps = {
  action: Action;
  child: ReactNode;
  formTab: NodeEditorTab;
};

type DiffCardProps = {
  action?: Action;
  child: ReactNode;
  formTab?: NodeEditorTab;
} & (
  | { pane: typeof panes.left; store: Store<State> }
  | { pane: typeof panes.right; store?: never }
);


/**
 * Builds the form for a card's action and provides it to child.
 */
function DiffCardForm({ action, child, formTab }: DiffCardFormProps) {
  const formMethods = useNodeEditorForm(action, formTab);

  if (!formMethods) return child;

  return (
    <FormProvider {...formMethods}>
      {child}
    </FormProvider>
  );
}

/**
 * Renders one instance of a card (left or right pane) for the split diff-view
 * for the "existing" and "modified" snapshots of the workflow.
 * Pass formTab when the child needs react-hook-form context (the NodeEditorDrawer
 * tabs) leave it off for consumers that don't (the outcome island cards).
 * - Right pane rendering required params: pane
 * - Left pane rendering required params: pane, store, formtab
 */
export function DiffCard({
  action, pane, store, child, formTab,
}: DiffCardProps) {
  if (!action) return <StyledDiffViewCardSlot pane={pane} aria-hidden />;
  if (pane === panes.left) {
    return (
      <StyledDiffViewCardSlot pane={pane}>
        <ReduxStoreProvider store={store}>
          {formTab
            ? <DiffCardForm action={action} child={child} formTab={formTab} />
            : child}
        </ReduxStoreProvider>
      </StyledDiffViewCardSlot>
    );
  }

  return (
    <StyledDiffViewCardSlot pane={pane}>
      {child}
    </StyledDiffViewCardSlot>
  );
}
